/******************************************** */
/* CUSTOMER */
/******************************************** */
export interface Customer {
  customer_id: string;
  name: string;
  code: string;
  metadata: Record<string, any>;
  global_lookup_id: string;
  projects_with_results: number;
  projects: number;
  created_at: string;
  created_by: string;
  created_by_name: string;
  updated_at: string;
  updated_by: string;
  updated_by_name: string;
}

export interface CustomersData {
  customers: Customer[];
}

export interface CustomerData {
  customer: Customer;
  lookups: Lookup[];
}

export interface CustomerInput {
  customer_id: string;
  name: string;
  code: string;
  metadata: Record<string, any>;
  global_lookup_id: string;
}

/******************************************** */
/* PROJECT */
/******************************************** */

export interface Project {
  current_upload: any;
  project_id: string;
  name: string;
  title: string;
  customer: {
    customer_id: string;
    name: string;
    code: string;
  };
  metadata: Record<string, any>;
  global_lookup_id: string;
  customer_lookup_id: string | null;
  created_at: string;
  created_by: string;
  created_by_name: string;
  updated_at: string;
  updated_by: string;
  updated_by_name: string;
  loaded_documents: {
    version: number;
    username: string | null;
    status: "finished" | "processing" | "uploaded" | "started" | "failed" | "unknown" | null;
    date: string | null;
    document_count: number;
  };
  processing_documents: {
    version: number;
    username: string | null;
    status: "finished" | "processing" | "uploaded" | "started" | "failed" | "unknown" | null;
    date: string | null;
    error: {
      code: string;
      message: string;
    } | null;
    document_count: number;
  };
  project_uploads_count: number;
}

export interface ProjectsData {
  projects: Project[];
}

export interface ProjectData {
  project: Project;
  lookups: Lookup[];
}

export interface ProjectInput {
  project_id: string;
  name: string;
  title: string;
  customer_id: string;
  metadata: Record<string, any>;
  global_lookup_id: string;
  customer_lookup_id?: string;
}

/******************************************** */
/* DOCUMENT */
/******************************************** */

export interface DocumentsData {
  documents: Document[];
}

export interface Document {
  document_id: string;
  customer_code: string;
  project_id: string;
  project: string;
  file_name: string; // User-friendly display name (like "Abe-2015.pdf")
  doc_path?: string; // Storage file name (like "CBq-8ZgBW4DRgsiTpnQi.pdf") - for matching, NOT for display
  doc_key: string;
  doc_title: string;
  stage: string;
}

/******************************************** */
/* USER */
/******************************************** */
export enum UserRole {
  ADMIN = "admin",
  EDITOR = "editor",
  VIEWER = "viewer",
}

export enum UserType {
  CUSTOMER = "customer",
  OH_USER = "oh-user",
}

export interface User {
  user_id: string;
  name: string;
  email: string;
  oid: string;
  user_type: UserType;
  user_role: UserRole;
  claims: UserClaims;
  metadata: Record<string, any>;
  customers: Customer[];
}

export interface UserClaims {
  canManageCustomers: boolean;
  canManageProjects: boolean;
  canManageUsers: boolean;
  canLoadProjectDocuments: boolean;
  canViewResults: boolean;
  canViewCodes: boolean;
}

export interface UsersData {
  users: User[];
}

export interface UserData {
  user: User;
}

export interface UserInput {
  user_id: string;
  name: string;
  email: string;
  oid: string;
  metadata: Record<string, any>;
  user_type: string;
  user_role: string;
  claims: string[];
  customers: string[];
}

export interface UserMetadataInput {
  results_explorer?: {
    active: "search" | "tree";
    tree: {
      view_by: number;
      filter: string;
    };
    search: {
      text: string;
      facets: {
        filters: string;
        order: string;
        sort_by: string;
        selected: string;
      };
      results: {
        sort_by: string;
        view_by: string;
        showing: number;
        page: number;
      };
    };
  };
  document_tabs?: Record<string, "content" | "chat">;
  [key: string]: any; // Allow additional properties
}

export interface ADUser {
  user_id: string;
  name: string;
  email: string;
  oid: string;
}

/******************************************** */
/* LOOKUP */
/******************************************** */

export interface LookupItem {
  id: string;
  name: string;
}

export interface Lookup {
  name: string;
  source: string;
  type: string;
  list: LookupItem[];
  can_override: boolean;
  level: string;
  order: number;
}

/******************************************** */
/* VIEW */
/******************************************** */

export interface ViewsData {
  views: View[];
}

export interface View {
  view_id: string;
  name: string;
  path: string[];
}

/******************************************** */
/* UPLOAD */
/******************************************** */

export interface ProjectUpload {
  project_upload_id: string;
  project_id: string;
  version: number;
  metadata: Record<string, any>;
  upload_by: string;
  upload_by_name: string;
  ready: boolean;
  document_count: number;
}

export interface ProjectUploadsData {
  project_uploads: ProjectUpload[];
}

/******************************************** */
/* RESULTS */
/******************************************** */

export interface ResultsData {
  results: Result[];
}

export interface Result {
  status?: string;
  customer_id: string;
  customer_code: string;
  project_id: string;
  project: string;
  indication: string;
  business_unit: string;
  therapeutic_area: string;
  doc_path: string;
  doc_filename?: string;
  doc_key?: string;
  document_id?: string;
  doc_title: string;
  stage: string;
  path: string[];
}

/******************************************** */
/* LOGGING */
/******************************************** */
export interface LogContext {
  userId?: string;
  operationName?: string;
  sessionId?: string;
  component?: "webapp" | "server";
}

export interface AuthLogContext extends LogContext {
  authMethod?: string;
  authSuccess?: boolean;
  authError?: string;
  oid?: string;
  userType?: string;
  userRole?: string;
}
